import React from 'react';
import { Calendar, Plane, DollarSign, Award } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DateInput } from './DateInput';
import { CityInput } from './CityInput';
import { City } from '../types';
import { createAlertsFromOpportunity } from '../services/alerts';

interface OpportunityFormData {
  origin: string;
  destination: string;
  departureDates: Date[];
  returnDates: Date[];
  value: number;
  miles: number;
  feesIncluded: boolean;
  loyaltyProgram: string;
}

interface ValidationErrors {
  departureDates?: string;
  returnDates?: string;
  value?: string;
}

export function OpportunityForm() {
  const [form, setForm] = React.useState<OpportunityFormData>({
    origin: '',
    destination: '',
    departureDates: [],
    returnDates: [],
    value: 0,
    miles: 0,
    feesIncluded: false,
    loyaltyProgram: '',
  });

  const [originCity, setOriginCity] = React.useState<City[]>([]);
  const [destinationCity, setDestinationCity] = React.useState<City[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>({});

  const validateDates = (departureDates: Date[], returnDates: Date[]) => {
    const errors: ValidationErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Validate departure dates
    if (departureDates.length === 0) {
      errors.departureDates = 'Selecione pelo menos uma data de ida';
    } else {
      for (const date of departureDates) {
        if (new Date(date) < today) {
          errors.departureDates = 'Data de ida não pode ser anterior a hoje';
          break;
        }
      }
    }

    // Validate return dates
    if (returnDates.length === 0) {
      errors.returnDates = 'Selecione pelo menos uma data de volta';
    } else {
      const earliestDeparture = new Date(Math.min(...departureDates.map(d => new Date(d).getTime())));
      for (const date of returnDates) {
        const returnDate = new Date(date);
        if (returnDate <= earliestDeparture) {
          errors.returnDates = 'Data de volta deve ser posterior à data de ida';
          break;
        }
      }
    }

    return errors;
  };

  const validateValue = (value: number) => {
    const errors: ValidationErrors = {};
    
    if (!value || value <= 0) {
      errors.value = 'O valor deve ser maior que zero';
    } else if (!/^\d+(\.\d{0,2})?$/.test(value.toString())) {
      errors.value = 'O valor deve ter no máximo 2 casas decimais';
    }

    return errors;
  };

  React.useEffect(() => {
    const dateErrors = validateDates(form.departureDates, form.returnDates);
    const valueErrors = validateValue(form.value);
    setValidationErrors({ ...dateErrors, ...valueErrors });
  }, [form.departureDates, form.returnDates, form.value]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validate all fields before submission
    const dateErrors = validateDates(form.departureDates, form.returnDates);
    const valueErrors = validateValue(form.value);
    const allErrors = { ...dateErrors, ...valueErrors };

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Log dates before conversion
      console.log('Original dates:', {
        departureDates: form.departureDates,
        returnDates: form.returnDates
      });

      // Converter as datas para o formato ISO
      const departureDatesISO = form.departureDates.map(date => {
        // Ensure we're working with Date objects
        const dateObj = new Date(date);
        return dateObj.toISOString();
      });
      const returnDatesISO = form.returnDates.map(date => {
        // Ensure we're working with Date objects
        const dateObj = new Date(date);
        return dateObj.toISOString();
      });

      // Log converted dates
      console.log('Converted dates:', {
        departureDatesISO,
        returnDatesISO
      });

      // Criar a oportunidade
      console.log('Submitting opportunity with dates:', {
        departureDatesISO,
        returnDatesISO
      });

      const { error: submitError } = await supabase
        .from('opportunities')
        .insert({
          origin: form.origin,
          destination: form.destination,
          departure_dates: departureDatesISO, // Now properly formatted
          return_dates: returnDatesISO, // Now properly formatted
          value: form.value,
          miles: form.miles,
          fees_included: form.feesIncluded,
          loyalty_program: form.loyaltyProgram,
          sent: false
        });

      if (submitError) {
        console.error('Error submitting opportunity:', submitError);
        throw submitError;
      }

      console.log('Opportunity created successfully, generating alerts...');

      // Criar alertas para usuários interessados
      const generatedAlerts = await createAlertsFromOpportunity(
        form.origin,
        form.destination,
        form.departureDates,
        form.returnDates,
        form.value,
        form.feesIncluded
      );

      console.log('Alert generation completed:', {
        alertsCount: generatedAlerts?.length || 0
      });

      setSuccess(true);
      // Reset form
      setForm({
        origin: '',
        destination: '',
        departureDates: [],
        returnDates: [],
        value: 0,
        miles: 0,
        feesIncluded: false,
        loyaltyProgram: '',
      });
      setOriginCity([]);
      setDestinationCity([]);
    } catch (err) {
      console.error('Error submitting opportunity:', err);
      setError('Erro ao cadastrar oportunidade');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddOriginCity = (cityName: string) => {
    const newCity = { id: crypto.randomUUID(), name: cityName };
    setOriginCity([newCity]);
    setForm(prev => ({
      ...prev,
      origin: cityName
    }));
  };

  const handleRemoveOriginCity = (cityId: string) => {
    setOriginCity([]);
    setForm(prev => ({
      ...prev,
      origin: ''
    }));
  };

  const handleAddDestinationCity = (cityName: string) => {
    const newCity = { id: crypto.randomUUID(), name: cityName };
    setDestinationCity([newCity]);
    setForm(prev => ({
      ...prev,
      destination: cityName
    }));
  };

  const handleRemoveDestinationCity = (cityId: string) => {
    setDestinationCity([]);
    setForm(prev => ({
      ...prev,
      destination: ''
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-100 text-green-700 rounded-lg">
          Oportunidade cadastrada com sucesso!
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Origem
          </label>
          <CityInput
            selectedCities={originCity}
            onAddCity={handleAddOriginCity}
            onRemoveCity={handleRemoveOriginCity}
            placeholder="Digite a cidade de origem"
            maxCities={1}
            isSingleSelect={true}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Destino
          </label>
          <CityInput
            selectedCities={destinationCity}
            onAddCity={handleAddDestinationCity}
            onRemoveCity={handleRemoveDestinationCity}
            placeholder="Digite a cidade de destino"
            maxCities={1}
            isSingleSelect={true}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Datas de Ida
          </label>
          <DateInput
            dates={form.departureDates}
            onChange={(dates) => setForm({ ...form, departureDates: dates })}
            error={validationErrors.departureDates}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Datas de Volta
          </label>
          <DateInput
            dates={form.returnDates}
            onChange={(dates) => setForm({ ...form, returnDates: dates })}
            error={validationErrors.returnDates}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Valor
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
              placeholder="0.00"
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                validationErrors.value 
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              } focus:ring-1`}
            />
            {validationErrors.value && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.value}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Milhas
          </label>
          <input
            required
            type="number"
            min="0"
            value={form.miles}
            onChange={(e) => setForm({ ...form, miles: Number(e.target.value) })}
            placeholder="Quantidade de milhas"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Programa de Fidelidade
          </label>
          <div className="relative">
            <Award className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              required
              type="text"
              value={form.loyaltyProgram}
              onChange={(e) => setForm({ ...form, loyaltyProgram: e.target.value })}
              placeholder="Ex: LATAM Pass"
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="feesIncluded"
            checked={form.feesIncluded}
            onChange={(e) => setForm({ ...form, feesIncluded: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="feesIncluded" className="ml-2 block text-sm text-gray-700">
            Taxas Inclusas
          </label>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          'Cadastrando...'
        ) : (
          <>
            <Plane className="rotate-90" size={20} />
            Cadastrar Oportunidade
          </>
        )}
      </button>
    </form>
  );
}